import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { agentRunsApi, knowledgeGapsApi } from "../services/api";
import { ComposedChart, Area, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { parseUtc } from "../utils/utcDate";
import { DEGRADED_ANALYSES_AGGREGATE_LABEL } from "../components/degradedAnalysisVocabulary";
import { taskTypeLabel } from "../components/taskTypeVocabulary";

const EDIT_TYPE_LABELS = {
  approve_with_edit:    "אישור עם תיקון",
  reject:               "דחייה",
  approve:              "אישור ללא שינוי",
  priority_change:      "שינוי עדיפות",
  action_change:        "שינוי פעולה",
  summary_edit:         "עריכת סיכום",
  field_correction:     "תיקון שדה",
  analysis_edit:        "תיקון אבחון",
  reply_edit:           "תיקון תגובה",
  action_edit:          "תיקון פעולה",
  closing_edit:         "תיקון סגירה",
  question_edit:        "תיקון שאלה",
  quality_rating:       "דירוג איכות",
};

const PERIOD_OPTIONS = [
  { value: "",   label: "כל הזמן" },
  { value: "1",  label: "היום" },
  { value: "7",  label: "7 ימים" },
  { value: "30", label: "30 ימים" },
  { value: "90", label: "90 ימים" },
];

function SectionLoadError({ text }) {
  return (
    <div role="alert" className="bg-white rounded-xl border border-gray-300 shadow-sm p-3 text-xs text-gray-600">
      {text}
    </div>
  );
}

/**
 * The one disclosure for this page's confidence figures (FULL-0062). `avg_confidence`,
 * `confidence_distribution` and `confidence_calibration` are all aggregates of
 * `AgentRun.analysis["confidence"]` computed by `GET /agent-runs/feedback/stats` — on the pilot
 * default that column holds `MockAnalysisAdapter`'s own derived number, so each of them reads as a
 * measurement of the fleet's AI while being a statistic of whichever adapter answered. The payload
 * now carries the denominator and the degraded share, and this draws them. It renders nothing when
 * the payload declares no degraded analysis, so a real fleet is not qualified — and nothing when the
 * payload predates the counts, so an absent declaration never reads as "none".
 *
 * `covers` names the figure the note sits with: the three are separate surfaces with their own
 * headings, so one note cannot qualify another.
 */
function ConfidenceProvenance({ stats, testId, covers }) {
  const total = stats?.analyses_total;
  const degraded = stats?.degraded_analyses;
  if (!degraded || !total) return null;
  return (
    <p
      className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1"
      data-testid={testId}
      title={`${covers} מחושב מ-${total} ניתוחים שמורים, מתוכם ${degraded} אינם אבחון אמיתי`}
    >
      <span className="font-semibold">{degraded}/{total}</span> {DEGRADED_ANALYSES_AGGREGATE_LABEL}
    </p>
  );
}

/**
 * The disclosure for a surface whose `ביטחון` figures are means of `AgentRun.analysis["confidence"]`
 * (FULL-0063, FULL-0064). The four payloads that feed one — `GET /agent-runs/stats/by-task-type`,
 * `/stats/by-company`, `/stats/by-technician` and `/stats/trend` — all return a bare array with no
 * `response_model`, so none can carry the window-level count `AgentRunStats` and `FeedbackStats` do.
 * Each row instead reports the population its own mean covers and the fallback share of that same
 * population, and the caller totals them over exactly the rows it draws.
 *
 * On the pilot default (no `ANALYSIS_PROVIDER`) that column holds `MockAnalysisAdapter`'s own capped
 * constant, so these figures read as measurements of the fleet's AI while being statistics of
 * whichever adapter answered. `prefix` names, in the note itself, what it qualifies — a note naming
 * nothing would let a reader attach the qualification to whichever figure they looked at first.
 *
 * It renders nothing when the rows declare no fallback content, so a real fleet is not qualified,
 * and nothing when the rows predate the counts, so an absent declaration never reads as "none".
 */
function RowsProvenance({ prefix, total, degraded, testId, covers }) {
  if (!degraded || !total) return null;
  return (
    <p
      className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1"
      data-testid={testId}
      title={`${covers} — מחושב מ-${total} ניתוחים שמורים, מתוכם ${degraded} אינם אבחון אמיתי`}
    >
      {prefix} — <span className="font-semibold">{degraded}/{total}</span> {DEGRADED_ANALYSES_AGGREGATE_LABEL}
    </p>
  );
}

function QualityRing({ rate }) {
  const pct = Math.round((rate ?? 0) * 100);
  const color = pct <= 15 ? "text-green-600" : pct <= 35 ? "text-amber-500" : "text-red-500";
  const label = pct <= 15 ? "טובה" : pct <= 35 ? "בינונית" : "טעונה שיפור";
  return (
    <div className="flex flex-col items-center gap-1">
      <div className={`text-4xl font-bold ${color}`}>{pct}%</div>
      <div className={`text-xs font-medium ${color}`}>{label}</div>
      <div className="text-[10px] text-gray-400">שיעור תיקונים</div>
    </div>
  );
}

function HBar({ label, value, max, color = "bg-brand-500" }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-36 truncate text-gray-700 text-xs text-right shrink-0">{label}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
        <div className={`h-2 rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-500 w-8 text-left shrink-0">{value}</span>
    </div>
  );
}

export default function FeedbackPage() {
  const [sinceDays, setSinceDays] = useState("");
  const [trendDays, setTrendDays] = useState(7);
  const [correctionEditType, setCorrectionEditType] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["feedback-stats", sinceDays],
    queryFn: () => agentRunsApi.feedbackStats(sinceDays ? parseInt(sinceDays) : undefined),
    refetchInterval: 60000,
  });

  const { data: trend = [], isError: trendFailed } = useQuery({
    queryKey: ["runs-trend", trendDays],
    queryFn: () => agentRunsApi.trend(trendDays),
    refetchInterval: 300000,
  });

  const { data: taskTypeStats = [], isError: taskTypeStatsFailed } = useQuery({
    queryKey: ["stats-by-task-type", sinceDays],
    queryFn: () => agentRunsApi.statsByTaskType(sinceDays ? { since_days: parseInt(sinceDays) } : {}),
    refetchInterval: 120000,
  });

  const { data: companyStats = [], isError: companyStatsFailed } = useQuery({
    queryKey: ["stats-by-company", sinceDays],
    queryFn: () => agentRunsApi.statsByCompany(sinceDays ? { since_days: parseInt(sinceDays) } : {}),
    refetchInterval: 120000,
  });

  const { data: technicianStats = [], isError: technicianStatsFailed } = useQuery({
    queryKey: ["stats-by-technician", sinceDays],
    queryFn: () => agentRunsApi.statsByTechnician(sinceDays ? { since_days: parseInt(sinceDays) } : {}),
    refetchInterval: 120000,
  });

  const { data: resolutionImpact, isError: resolutionImpactFailed } = useQuery({
    queryKey: ["resolution-impact", sinceDays],
    queryFn: () => agentRunsApi.resolutionImpact(sinceDays ? { since_days: parseInt(sinceDays) } : {}),
    refetchInterval: 300000,
  });

  const { data: topGaps = [], isError: topGapsFailed } = useQuery({
    queryKey: ["top-knowledge-gaps"],
    queryFn: () => knowledgeGapsApi.list({ status_filter: "open" }),
    staleTime: 120_000,
    refetchInterval: 300000,
    select: (d) => (Array.isArray(d) ? d : []).slice(0, 5),
  });

  const { data: recentCorrections = [], isError: recentCorrectionsFailed } = useQuery({
    queryKey: ["recent-feedback", sinceDays],
    queryFn: () => agentRunsApi.recentFeedback(sinceDays ? parseInt(sinceDays) : 30, 20),
    refetchInterval: 120000,
  });

  // /feedback/stats sends edit_rate and reason_coverage as PERCENTAGES (0-100), while every other rate on
  // this page (adoption, per-company/per-task edit_rate, thumbs-up) is a 0-1 fraction; normalise once here.
  const editRate = data?.edit_rate != null ? data.edit_rate / 100 : null;
  const reasonCoverage = (data?.reason_coverage ?? 0) / 100;
  const editTypes = Object.entries(data?.by_edit_type ?? {}).sort((a, b) => b[1] - a[1]);
  const taskTypes = Object.entries(data?.edits_by_task_type ?? {}).sort((a, b) => b[1] - a[1]);
  const maxEditType = editTypes.length ? editTypes[0][1] : 1;
  const maxTaskType = taskTypes.length ? taskTypes[0][1] : 1;

  // Compute recommended actions from all loaded data
  const actions = [];
  if (data && data.total_edits > 0) {
    const worstTaskType = taskTypeStats.find((r) => r.edit_rate != null && r.edit_rate >= 0.3 && r.total >= 3);
    if (worstTaskType) {
      actions.push({
        icon: "📝",
        text: `נושא "${taskTypeLabel(worstTaskType.task_type)}" — ${Math.round(worstTaskType.edit_rate * 100)}% שיעור תיקון. מומלץ ליצור מאמר ידע.`,
        // The keyword stays the raw token on purpose: this is a search query, not a label, so
        // wording it would look for Hebrew prose instead of the analysis theme.
        to: `/knowledge?keyword=${encodeURIComponent(worstTaskType.task_type)}`,
        label: "צור מאמר",
        color: "border-amber-200 bg-amber-50 text-amber-800",
        // The subject this recommends an article for is `analysis["task_type"]`, not a field of the
        // ticket, so the recommendation carries the provenance of the analyses it was read out of.
        taskTypeProvenance: {
          total: worstTaskType.confidence_analyses || 0,
          degraded: worstTaskType.degraded_analyses || 0,
        },
      });
    }
    const worstCompany = companyStats.find((r) => r.edit_rate != null && r.edit_rate >= 0.5 && r.total >= 3);
    if (worstCompany && worstCompany.company_id) {
      actions.push({
        icon: "🏢",
        text: `חברת "${worstCompany.company_name}" — ${Math.round(worstCompany.edit_rate * 100)}% שיעור תיקון. בדוק מאמרי ידע ייעודיים.`,
        to: `/companies/${worstCompany.company_id}/profile`,
        label: "פרופיל חברה",
        color: "border-red-200 bg-red-50 text-red-800",
      });
    }
    if (reasonCoverage < 0.25 && data.total_edits >= 5) {
      actions.push({
        icon: "💬",
        text: `רק ${Math.round(reasonCoverage * 100)}% מהתיקונים כוללים הסבר. עודדו טכנאים לסמן סיבה.`,
        to: null,
        label: null,
        color: "border-violet-200 bg-violet-50 text-violet-800",
      });
    }
  }
  if (data && data.adoption_rate != null && data.adoption_rate < 0.2 && data.total_tickets >= 5) {
    actions.push({
      icon: "🤖",
      text: `שיעור אימוץ AI נמוך — ${Math.round(data.adoption_rate * 100)}% מהקריאות עוברות ניתוח AI.`,
      to: "/agent-runs",
      label: "ריצות AI",
      color: "border-blue-200 bg-blue-50 text-blue-800",
    });
  }

  // The rows the task-type card actually draws, and the population their `ביטחון` means cover.
  // Totalled over the drawn rows rather than over every row the endpoint returned, because the note
  // has to qualify the figures on screen and not rows the slice removed — and per row rather than as
  // a window count because a second endpoint's window would be a second author for one fact.
  const shownTaskTypeStats = taskTypeStats.slice(0, 12);
  const taskTypeAnalyses = shownTaskTypeStats.reduce((n, r) => n + (r.confidence_analyses || 0), 0);
  const taskTypeDegraded = shownTaskTypeStats.reduce((n, r) => n + (r.degraded_analyses || 0), 0);

  // The `ביטחון` figures on the two tables below and on the trend chart are the same kind of number:
  // `avg(analysis["confidence"])`, from payloads that carry no window count either. Same rule as the
  // task-type card — totalled over exactly the rows each surface draws, so the note qualifies the
  // figures on screen and not the ones a slice removed.
  const shownCompanyStats = companyStats.slice(0, 15);
  const companyAnalyses = shownCompanyStats.reduce((n, r) => n + (r.confidence_analyses || 0), 0);
  const companyDegraded = shownCompanyStats.reduce((n, r) => n + (r.degraded_analyses || 0), 0);

  const technicianAnalyses = technicianStats.reduce((n, r) => n + (r.confidence_analyses || 0), 0);
  const technicianDegraded = technicianStats.reduce((n, r) => n + (r.degraded_analyses || 0), 0);

  const trendAnalyses = trend.reduce((n, r) => n + (r.confidence_analyses || 0), 0);
  const trendDegraded = trend.reduce((n, r) => n + (r.degraded_analyses || 0), 0);

  return (
    <div className="max-w-5xl mx-auto space-y-5" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">איכות AI 📊</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            ניתוח תיקונים של טכנאים — סיגנל למידה לשיפור הסוכנים
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <span>טווח</span>
          <select
            value={sinceDays}
            onChange={(e) => setSinceDays(e.target.value)}
            className="border rounded-lg px-3 py-2 bg-white"
          >
            {PERIOD_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Recommended actions */}
      {actions.length > 0 && (
        <div className="bg-white rounded-xl border shadow-sm p-4 space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">פעולות מומלצות</p>
          {actions.map((action, i) => (
            <div key={i} className={`rounded-lg border px-3 py-2 text-sm ${action.color}`}>
              <div className="flex items-center gap-3">
                <span className="text-base shrink-0">{action.icon}</span>
                <span className="flex-1">{action.text}</span>
                {action.to && action.label && (
                  <Link
                    to={action.to}
                    className="shrink-0 text-xs font-medium underline hover:no-underline"
                  >
                    {action.label} →
                  </Link>
                )}
              </div>
              {action.taskTypeProvenance && (
                <RowsProvenance
                  prefix="הנושא בהמלצה זו"
                  total={action.taskTypeProvenance.total}
                  degraded={action.taskTypeProvenance.degraded}
                  testId="task-type-action-provenance"
                  covers="הנושא שבהמלצה זו"
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Recurring knowledge gaps */}
      {topGapsFailed && <SectionLoadError text="לא ניתן לטעון את פערי הידע החוזרים" />}
      {topGaps.length > 0 && (
        <div className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h2 className="font-semibold text-gray-700 text-sm">פערי ידע חוזרים</h2>
              <p className="text-xs text-gray-400">מידע שה-AI ביקש ולא מצא — צור מאמר ידע לשיפור הדיוק</p>
            </div>
            <Link to="/knowledge" className="text-xs text-violet-600 hover:underline shrink-0">כל הפערים →</Link>
          </div>
          <div className="space-y-1.5">
            {topGaps.map((gap) => (
              <div key={gap.id} className="flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                <span className="text-xs text-gray-500 shrink-0 min-w-[2rem] text-center font-semibold">{gap.occurrence_count}×</span>
                <span className="flex-1 text-xs text-gray-700 truncate" title={gap.gap_text}>{gap.gap_text}</span>
                <Link
                  to={`/knowledge?keyword=${encodeURIComponent(gap.gap_text.slice(0, 100))}`}
                  className="shrink-0 text-xs text-violet-600 bg-violet-50 border border-violet-200 rounded-full px-2 py-0.5 hover:bg-violet-100 transition-colors"
                >
                  + מאמר
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trend chart */}
      {trendFailed && <SectionLoadError text="לא ניתן לטעון את נתוני המגמה" />}
      {trend.length > 0 && (
        <div className="bg-white rounded-xl border shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-gray-700 text-sm">מגמה</h2>
            <div className="flex gap-1">
              {[7, 14, 30].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setTrendDays(d)}
                  className={`text-xs px-2 py-0.5 rounded border transition-colors ${
                    trendDays === d
                      ? "bg-indigo-100 border-indigo-400 text-indigo-700 font-medium"
                      : "bg-gray-50 border-gray-200 text-gray-500 hover:border-indigo-300 hover:text-indigo-600"
                  }`}
                >
                  {d}י
                </button>
              ))}
            </div>
          </div>
          <RowsProvenance
            prefix="קו הביטחון במגמה"
            total={trendAnalyses}
            degraded={trendDegraded}
            testId="trend-provenance"
            covers="גרף המגמה"
          />
          <ResponsiveContainer width="100%" height={150}>
            <ComposedChart data={trend} margin={{ top: 4, right: 40, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gradApproved" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#16a34a" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#16a34a" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: "#9ca3af" }}
                tickFormatter={(d) => {
                  const dt = new Date(d);
                  return `${dt.getDate()}/${dt.getMonth() + 1}`;
                }}
              />
              <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "#9ca3af" }} allowDecimals={false} />
              <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 10, fill: "#f59e0b" }} unit="%" width={30} />
              <Tooltip
                contentStyle={{ fontSize: 11, borderRadius: 6, border: "1px solid #e5e7eb" }}
                formatter={(value, name) => {
                  if (name === "avg_confidence") return [`${Math.round(value)}%`, "ביטחון"];
                  if (name === "total") return [value, "ריצות"];
                  if (name === "approved") return [value, "אושרו"];
                  return [value, "נדחו"];
                }}
                labelFormatter={(d) => new Date(d).toLocaleDateString("he-IL")}
              />
              <Area yAxisId="left" type="monotone" dataKey="total" stroke="#6366f1" fill="url(#gradTotal)" strokeWidth={2} dot={false} name="total" />
              <Area yAxisId="left" type="monotone" dataKey="approved" stroke="#16a34a" fill="url(#gradApproved)" strokeWidth={2} dot={false} name="approved" />
              <Area yAxisId="left" type="monotone" dataKey="rejected" stroke="#ef4444" fill="none" strokeWidth={1.5} strokeDasharray="4 2" dot={false} name="rejected" />
              <Line yAxisId="right" type="monotone" dataKey="avg_confidence" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3, fill: "#f59e0b" }} connectNulls name="avg_confidence" />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="flex gap-4 mt-2 text-xs text-gray-400 justify-center">
            <span><span className="inline-block w-3 h-0.5 bg-indigo-500 mr-1" />ריצות</span>
            <span><span className="inline-block w-3 h-0.5 bg-green-600 mr-1" />אושרו</span>
            <span><span className="inline-block w-3 h-0.5 bg-red-400 border-dashed mr-1" style={{borderTop: "2px dashed #f87171", background: "none"}} />נדחו</span>
            <span><span className="inline-block w-3 h-0.5 bg-amber-500 mr-1" />ביטחון%</span>
          </div>
        </div>
      )}

      {/* Executed Actions breakdown */}
      {data && Object.keys(data.executed_actions ?? {}).length > 0 && (() => {
        const ea = data.executed_actions;
        const total = Object.values(ea).reduce((s, v) => s + v, 0);
        const ACTION_META = {
          close_ticket:         { label: "קריאות שנסגרו", icon: "✓", color: "bg-green-50 border-green-200 text-green-800", minPerAction: 15 },
          send_customer_message:{ label: "הודעות ללקוח", icon: "✉", color: "bg-blue-50 border-blue-200 text-blue-800", minPerAction: 8 },
          escalate:             { label: "הסלמות", icon: "⬆", color: "bg-red-50 border-red-200 text-red-800", minPerAction: 5 },
          add_comment:          { label: "הערות פנימיות", icon: "💬", color: "bg-gray-50 border-gray-200 text-gray-700", minPerAction: 5 },
        };
        const totalMinSaved = Object.entries(ACTION_META).reduce(
          (s, [key, meta]) => s + (ea[key] ?? 0) * meta.minPerAction, 0
        );
        const roiLabel = totalMinSaved >= 60
          ? `${(totalMinSaved / 60).toFixed(1)} שעות`
          : `${totalMinSaved} דקות`;
        return (
          <div className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <h2 className="font-semibold text-gray-700 text-sm">פעולות שבוצעו על ידי AI</h2>
                <p className="text-xs text-gray-400">פעולות שאושרו ובוצעו אוטומטית</p>
              </div>
              <span className="text-xs font-semibold text-gray-500 bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5">{total} סה"כ</span>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {Object.entries(ACTION_META).map(([key, meta]) => {
                const cnt = ea[key] ?? 0;
                return (
                  <div key={key} className={`flex flex-col items-center rounded-lg border px-3 py-2.5 ${meta.color} ${cnt === 0 ? "opacity-40" : ""}`}>
                    <span className="text-lg leading-none">{meta.icon}</span>
                    <span className="text-2xl font-bold mt-1">{cnt}</span>
                    <span className="text-[10px] text-center mt-0.5 leading-tight">{meta.label}</span>
                  </div>
                );
              })}
            </div>
            {totalMinSaved > 0 && (
              <div className="flex items-center justify-between rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 gap-3 flex-wrap">
                <span className="text-xs text-emerald-700">חיסכון משוער בזמן טיפול</span>
                <span className="text-sm font-bold text-emerald-700">~{roiLabel}</span>
                {data.total_cost_usd != null && (
                  <span className="text-xs text-emerald-600 opacity-70">
                    עלות: ${data.total_cost_usd < 0.01 ? data.total_cost_usd.toFixed(4) : data.total_cost_usd.toFixed(2)}
                  </span>
                )}
                <span className="text-[10px] text-emerald-500 hidden sm:block">15ד׳/סגירה · 8ד׳/מייל · 5ד׳/הערה</span>
              </div>
            )}
          </div>
        );
      })()}

      {/* AI Adoption — shown regardless of feedback data */}
      {data && (data.total_tickets > 0 || data.tickets_with_ai > 0) && (
        <div className="bg-white rounded-xl border shadow-sm p-5">
          <div className="flex items-center justify-between gap-2 mb-3">
            <h2 className="font-semibold text-gray-700 text-sm">כיסוי AI</h2>
            <span className="text-[11px] text-gray-400">אחוז קריאות שעברו ניתוח AI בתקופה</span>
          </div>
          <div className="flex items-center gap-6 flex-wrap">
            <div className="text-center min-w-[5rem]">
              <div className={`text-3xl font-bold ${
                (data.adoption_rate ?? 0) >= 0.5 ? "text-green-600" :
                (data.adoption_rate ?? 0) >= 0.2 ? "text-amber-500" : "text-red-500"
              }`}>
                {data.adoption_rate != null ? `${Math.round(data.adoption_rate * 100)}%` : "—"}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">שיעור אימוץ</div>
            </div>
            <div className="flex-1 space-y-1">
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <div className="w-32 bg-gray-100 rounded-full h-2.5 overflow-hidden">
                  <div
                    className="h-2.5 rounded-full bg-violet-500"
                    style={{ width: `${data.total_tickets > 0 ? Math.round((data.tickets_with_ai / data.total_tickets) * 100) : 0}%` }}
                  />
                </div>
                <span>{data.tickets_with_ai} מתוך {data.total_tickets} קריאות</span>
              </div>
              {data.adoption_rate != null && data.adoption_rate < 0.3 && data.total_tickets > 5 && (
                <p className="text-[11px] text-amber-600">
                  AI מנתח רק חלק מהקריאות. עודדו טכנאים להפעיל ניתוח AI על קריאות נוספות.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quality ratings (thumbs up/down) */}
      {data && data.quality_ratings_total > 0 && (
        <div className="bg-white rounded-xl border shadow-sm p-5">
          <div className="flex items-center justify-between gap-2 mb-3">
            <h2 className="font-semibold text-gray-700 text-sm">דירוג איכות AI (👍/👎)</h2>
            <span className="text-[11px] text-gray-400">{data.quality_ratings_total} דירוגים</span>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center min-w-[5rem]">
              <div className={`text-3xl font-bold ${
                (data.quality_thumbs_up_rate ?? 0) >= 0.75 ? "text-green-600"
                : (data.quality_thumbs_up_rate ?? 0) >= 0.5 ? "text-amber-500"
                : "text-red-500"
              }`}>
                {data.quality_thumbs_up_rate != null ? `${Math.round(data.quality_thumbs_up_rate * 100)}%` : "—"}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">👍 חיוביים</div>
            </div>
            <div className="flex-1 space-y-1">
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <div className="w-32 bg-gray-100 rounded-full h-2.5 overflow-hidden">
                  <div
                    className="h-2.5 rounded-full bg-green-500"
                    style={{ width: `${data.quality_ratings_total > 0 ? Math.round((data.quality_thumbs_up / data.quality_ratings_total) * 100) : 0}%` }}
                  />
                </div>
                <span>{data.quality_thumbs_up} 👍 / {data.quality_ratings_total - data.quality_thumbs_up} 👎</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resolution time impact */}
      {resolutionImpactFailed && <SectionLoadError text="לא ניתן לטעון את נתוני השפעת ה-AI על זמן הפתרון" />}
      {resolutionImpact && (resolutionImpact.count_with_ai > 0 || resolutionImpact.count_without_ai > 0) && (
        <div className="bg-white rounded-xl border shadow-sm p-5">
          <h2 className="font-semibold text-gray-700 text-sm mb-3">השפעת AI על זמן פתרון קריאות</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg bg-violet-50 border border-violet-100 p-3 text-center">
              <p className="text-xs text-violet-500 mb-1">עם ניתוח AI ({resolutionImpact.count_with_ai} קריאות)</p>
              <p className="text-xl font-bold text-violet-700">
                {resolutionImpact.avg_hours_with_ai != null
                  ? resolutionImpact.avg_hours_with_ai < 1
                    ? `${Math.round(resolutionImpact.avg_hours_with_ai * 60)}ד`
                    : `${resolutionImpact.avg_hours_with_ai.toFixed(1)}ש`
                  : "—"}
              </p>
              <p className="text-[10px] text-violet-400 mt-0.5">זמן סגירה ממוצע</p>
            </div>
            <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-center">
              <p className="text-xs text-gray-400 mb-1">ללא AI ({resolutionImpact.count_without_ai} קריאות)</p>
              <p className="text-xl font-bold text-gray-600">
                {resolutionImpact.avg_hours_without_ai != null
                  ? resolutionImpact.avg_hours_without_ai < 1
                    ? `${Math.round(resolutionImpact.avg_hours_without_ai * 60)}ד`
                    : `${resolutionImpact.avg_hours_without_ai.toFixed(1)}ש`
                  : "—"}
              </p>
              <p className="text-[10px] text-gray-400 mt-0.5">זמן סגירה ממוצע</p>
            </div>
          </div>
          {resolutionImpact.hours_saved != null && resolutionImpact.hours_saved > 0 && (
            <p className="text-xs text-green-700 bg-green-50 border border-green-100 rounded px-3 py-2 mt-3 text-center">
              AI מקצר זמן סגירה ב־{resolutionImpact.hours_saved < 1 ? `${Math.round(resolutionImpact.hours_saved * 60)} דקות` : `${resolutionImpact.hours_saved.toFixed(1)} שעות`} בממוצע
            </p>
          )}
          {resolutionImpact.hours_saved != null && resolutionImpact.hours_saved < 0 && (
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded px-3 py-2 mt-3 text-center">
              קריאות עם AI לוקחות {Math.abs(resolutionImpact.hours_saved).toFixed(1)}ש יותר — ייתכן שהן מורכבות יותר מהממוצע
            </p>
          )}
        </div>
      )}

      {/* Task type performance — shown regardless of feedback data */}
      {taskTypeStatsFailed && <SectionLoadError text="לא ניתן לטעון את הנתונים לפי סוג משימה" />}
      {taskTypeStats.length > 0 && (
        <div className="bg-white rounded-xl border shadow-sm p-5 space-y-3">
          <h2 className="font-semibold text-gray-700 text-sm">ביצועי AI לפי נושא משימה</h2>
          <p className="text-xs text-gray-400">נושאים שהAI מטפל בהם — דירוג לפי נפח. עריכות נמוכות = AI מדויק בנושא.</p>
          <RowsProvenance
            prefix="הנושא והביטחון בטבלה זו"
            total={taskTypeAnalyses}
            degraded={taskTypeDegraded}
            testId="task-type-provenance"
            covers="ביצועי AI לפי נושא משימה"
          />          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-400 border-b border-gray-100">
                  <th className="text-right py-1 font-medium">נושא</th>
                  <th className="text-center py-1 font-medium">ריצות</th>
                  <th className="text-center py-1 font-medium">הושלמו</th>
                  <th className="text-center py-1 font-medium">ביטחון</th>
                  <th className="text-center py-1 font-medium">נדחו</th>
                  <th className="text-center py-1 font-medium">שיעור עריכה</th>
                  <th className="text-center py-1 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {shownTaskTypeStats.map((row) => {
                  const editPct = row.edit_rate != null ? Math.round(row.edit_rate * 100) : null;
                  const editCls = editPct == null ? "text-gray-300" : editPct <= 15 ? "text-green-600" : editPct <= 35 ? "text-amber-500" : "text-red-500";
                  const needsKb = editPct != null && editPct >= 20;
                  const confCls = row.avg_confidence == null ? "text-gray-300" : row.avg_confidence >= 70 ? "text-green-600" : row.avg_confidence >= 50 ? "text-amber-500" : "text-red-500";
                  const rejPct = row.total > 0 ? Math.round((row.rejected / row.total) * 100) : null;
                  const rejCls = rejPct == null ? "text-gray-300" : rejPct <= 10 ? "text-green-600" : rejPct <= 25 ? "text-amber-500" : "text-red-500";
                  return (
                    <tr key={row.task_type} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-1.5 text-gray-700 max-w-[10rem] truncate">{taskTypeLabel(row.task_type)}</td>
                      <td className="py-1.5 text-center text-gray-600">{row.total}</td>
                      <td className="py-1.5 text-center">
                        <span className={row.completion_rate >= 0.7 ? "text-green-600" : row.completion_rate >= 0.5 ? "text-amber-500" : "text-red-500"}>
                          {row.completion_rate != null ? `${Math.round(row.completion_rate * 100)}%` : "—"}
                        </span>
                      </td>
                      <td className={`py-1.5 text-center font-medium ${confCls}`}>
                        {row.avg_confidence != null ? `${Math.round(row.avg_confidence)}%` : "—"}
                      </td>
                      <td className={`py-1.5 text-center font-medium ${rejCls}`} title={row.rejected != null ? `${row.rejected} ריצות נדחו` : ""}>
                        {rejPct != null ? `${rejPct}%` : "—"}
                      </td>
                      <td className={`py-1.5 text-center font-medium ${editCls}`}>
                        {editPct != null ? `${editPct}%` : "—"}
                      </td>
                      <td className="py-1.5 text-center">
                        {needsKb && (
                          <Link
                            to={`/knowledge?keyword=${encodeURIComponent(row.task_type)}`}
                            className="text-xs text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-full px-1.5 py-0.5 transition-colors"
                            title="צור מאמר ידע לנושא זה"
                          >
                            📝
                          </Link>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
        </div>
      ) : error && !data ? (        // a failed background refetch keeps the loaded numbers on screen
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">
          שגיאה בטעינת נתוני משוב
        </div>
      ) : !data || data.total_edits === 0 ? (
        <div className="bg-white rounded-xl border shadow-sm p-8 text-center text-gray-400">
          <p className="text-3xl mb-3">📊</p>
          <p>אין עדיין נתוני משוב לתצוגה. הנתונים יופיעו לאחר שטכנאים יאשרו ויתקנו ריצות.</p>
        </div>
      ) : (
        <>
          {/* Top stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="bg-white rounded-xl border shadow-sm p-4 flex flex-col items-center gap-1">
              <QualityRing rate={editRate} />
            </div>
            <div className="bg-white rounded-xl border shadow-sm p-4 text-center">
              <div className="text-2xl font-bold text-gray-800">{data.total_edits}</div>
              <div className="text-xs text-gray-400 mt-0.5">תיקונים סה"כ</div>
            </div>
            <div className="bg-white rounded-xl border shadow-sm p-4 text-center">
              <div className="text-2xl font-bold text-gray-800">{data.edited_runs}</div>
              <div className="text-xs text-gray-400 mt-0.5">ריצות עם תיקון</div>
              <div className="text-[10px] text-gray-300 mt-0.5">מתוך {data.completed_runs} שאושרו</div>
            </div>
            <div className="bg-white rounded-xl border shadow-sm p-4 text-center">
              <div className={`text-2xl font-bold ${
                reasonCoverage >= 0.5 ? "text-green-600" :
                reasonCoverage >= 0.2 ? "text-amber-500" : "text-red-500"
              }`}>
                {Math.round(reasonCoverage * 100)}%
              </div>
              <div className="text-xs text-gray-400 mt-0.5">כיסוי הסבר</div>
              <div className="text-[10px] text-gray-300 mt-0.5">{data.reasoned_edits} עם סיבה</div>
            </div>
            <div className="bg-white rounded-xl border shadow-sm p-4 text-center">
              <div className={`text-2xl font-bold ${
                data.avg_confidence == null ? "text-gray-300"
                : data.avg_confidence >= 70 ? "text-green-600"
                : data.avg_confidence >= 50 ? "text-amber-500"
                : "text-red-500"
              }`}>
                {data.avg_confidence != null ? `${data.avg_confidence}%` : "—"}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">ביטחון ממוצע</div>
              <ConfidenceProvenance
                stats={data}
                testId="confidence-tile-provenance"
                covers="ביטחון ממוצע"
              />
            </div>
            <div className="bg-white rounded-xl border shadow-sm p-4 text-center">
              <div className={`text-2xl font-bold ${
                data.avg_resolution_minutes == null ? "text-gray-300"
                : data.avg_resolution_minutes < 10 ? "text-green-600"
                : data.avg_resolution_minutes < 30 ? "text-amber-500"
                : "text-red-500"
              }`}>
                {data.avg_resolution_minutes != null
                  ? data.avg_resolution_minutes < 60
                    ? `${Math.round(data.avg_resolution_minutes)}ד`
                    : `${(data.avg_resolution_minutes / 60).toFixed(1)}ש`
                  : "—"}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">זמן ריצה ממוצע</div>
            </div>
          </div>

          {/* Confidence distribution */}
          {data.confidence_distribution && Object.keys(data.confidence_distribution).length > 0 && (() => {
            const BUCKETS = [
              { key: "90+",   label: "90%+",   cls: "bg-green-500" },
              { key: "75-89", label: "75–89%", cls: "bg-green-400" },
              { key: "60-74", label: "60–74%", cls: "bg-amber-400" },
              { key: "40-59", label: "40–59%", cls: "bg-orange-400" },
              { key: "<40",   label: "<40%",   cls: "bg-red-500" },
            ];
            const total = Object.values(data.confidence_distribution).reduce((s, v) => s + v, 0);
            if (!total) return null;
            return (
              <div className="bg-white rounded-xl border shadow-sm p-5 space-y-2">
                <h2 className="font-semibold text-gray-700 text-sm">התפלגות ביטחון AI</h2>
                <ConfidenceProvenance
                  stats={data}
                  testId="confidence-distribution-provenance"
                  covers="התפלגות ביטחון AI"
                />
                <div className="flex gap-1 h-6 rounded-full overflow-hidden">
                  {BUCKETS.map(({ key, cls }) => {
                    const cnt = data.confidence_distribution[key] || 0;
                    const pct = Math.round(cnt / total * 100);
                    return pct > 0 ? (
                      <div key={key} className={`${cls} transition-all`} style={{ width: `${pct}%` }} title={`${key}: ${cnt} ריצות (${pct}%)`} />
                    ) : null;
                  })}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mt-1">
                  {BUCKETS.map(({ key, label, cls }) => {
                    const cnt = data.confidence_distribution[key] || 0;
                    if (!cnt) return null;
                    const pct = Math.round(cnt / total * 100);
                    return (
                      <span key={key} className="flex items-center gap-1">
                        <span className={`inline-block w-2.5 h-2.5 rounded-sm ${cls}`} />
                        {label}: {pct}% ({cnt})
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Confidence calibration — rejection rate per confidence bucket */}
          {(data?.confidence_calibration?.length ?? 0) >= 2 && (() => {
            const cal = data.confidence_calibration;
            const maxRate = Math.max(...cal.map(r => r.rejection_rate), 10);
            return (
              <div className="bg-white rounded-xl border shadow-sm p-5 space-y-3">
                <h2 className="font-semibold text-gray-700 text-sm">כיול ביטחון — שיעור דחיות לפי טווח ביטחון</h2>
                <p className="text-xs text-gray-400">עמודה גבוהה בטווח ביטחון גבוה מצביעה על הערכת-יתר של ה-AI</p>
                <ConfidenceProvenance
                  stats={data}
                  testId="confidence-calibration-provenance"
                  covers="כיול ביטחון"
                />
                <div className="flex items-end gap-2 h-32">
                  {cal.map(r => {
                    const barH = maxRate > 0 ? Math.round((r.rejection_rate / maxRate) * 100) : 0;
                    const color = r.rejection_rate >= 40 ? "bg-red-400"
                                : r.rejection_rate >= 20 ? "bg-amber-400"
                                : "bg-green-400";
                    return (
                      <div key={r.bucket} className="flex flex-col items-center flex-1 gap-1">
                        <span className="text-[10px] text-gray-500 font-medium">{r.rejection_rate}%</span>
                        <div className="w-full flex flex-col justify-end" style={{ height: "80px" }}>
                          <div
                            className={`w-full rounded-t ${color} transition-all`}
                            style={{ height: `${Math.max(barH, 2)}%` }}
                            title={`${r.label}: ${r.rejected}/${r.total} נדחו (${r.rejection_rate}%)`}
                          />
                        </div>
                        <span className="text-[9px] text-gray-400 text-center leading-tight">{r.label}</span>
                        <span className="text-[9px] text-gray-300">n={r.total}</span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-gray-400 text-left">ירוק = כיול טוב · צהוב = הערכת-יתר מתונה · אדום = הערכת-יתר חמורה</p>
              </div>
            );
          })()}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Edit types */}
            {editTypes.length > 0 && (
              <div className="bg-white rounded-xl border shadow-sm p-5 space-y-3">
                <h2 className="font-semibold text-gray-700 text-sm">סוגי תיקונים</h2>
                <div className="space-y-2">
                  {editTypes.map(([type, count]) => (
                    <HBar
                      key={type}
                      label={EDIT_TYPE_LABELS[type] ?? type}
                      value={count}
                      max={maxEditType}
                      color="bg-violet-500"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Task types */}
            {taskTypes.length > 0 && (
              <div className="bg-white rounded-xl border shadow-sm p-5 space-y-3">
                <h2 className="font-semibold text-gray-700 text-sm">תיקונים לפי סוג משימה</h2>
                <div className="space-y-2">
                  {taskTypes.map(([type, count]) => (
                    <HBar
                      key={type}
                      label={taskTypeLabel(type)}
                      value={count}
                      max={maxTaskType}
                      color="bg-amber-400"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Top technicians */}
            {data.top_technicians?.length > 0 && (
              <div className="bg-white rounded-xl border shadow-sm p-5 space-y-3">
                <h2 className="font-semibold text-gray-700 text-sm">תורמי משוב מובילים</h2>
                <div className="space-y-2">
                  {data.top_technicians.slice(0, 8).map((t, i) => (
                    <div key={t.technician_id ?? i} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 truncate max-w-[60%]">
                        {t.display_name || t.email || t.technician_id}
                      </span>
                      <span className="text-xs bg-brand-50 text-brand-700 border border-brand-200 rounded-full px-2 py-0.5 shrink-0">
                        {t.count} תיקונים
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Rejection reasons */}
            {Object.keys(data.rejection_reason_distribution ?? {}).length > 0 && (
              <div className="bg-white rounded-xl border shadow-sm p-5 space-y-3">
                <h2 className="font-semibold text-gray-700 text-sm">סיבות דחייה נפוצות</h2>
                {(() => {
                  const entries = Object.entries(data.rejection_reason_distribution).sort((a, b) => b[1] - a[1]);
                  const maxR = entries[0]?.[1] ?? 1;
                  return (
                    <div className="space-y-2">
                      {entries.slice(0, 6).map(([reason, count]) => (
                        <HBar
                          key={reason}
                          label={reason}
                          value={count}
                          max={maxR}
                          color="bg-red-400"
                        />
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Signal quality callout */}
          {reasonCoverage < 0.3 && data.total_edits > 5 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 text-sm text-amber-800">
              <strong>טיפ לשיפור הלמידה:</strong> רק {Math.round(reasonCoverage * 100)}% מהתיקונים כוללים הסבר.
              עודדו טכנאים למלא את שדה "סיבה" בעת אישור עם תיקון — זה הסיגנל החשוב ביותר לשיפור הסוכנים.
            </div>
          )}
        </>
      )}

      {/* Per-company AI performance — shown regardless of feedback data */}
      {companyStatsFailed && <SectionLoadError text="לא ניתן לטעון את הנתונים לפי חברה" />}
      {companyStats.length > 0 && (
        <div className="bg-white rounded-xl border shadow-sm p-5 space-y-3">
          <h2 className="font-semibold text-gray-700 text-sm">ביצועי AI לפי חברה</h2>
          <p className="text-xs text-gray-400">חברות עם שיעור עריכה גבוה = AI פחות מדויק עבורן — כדאי ליצור מאמרי ידע ממוקדים.</p>
          <RowsProvenance
            prefix="הביטחון הממוצע בטבלה זו"
            total={companyAnalyses}
            degraded={companyDegraded}
            testId="company-provenance"
            covers="ביצועי AI לפי חברה"
          />
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-400 border-b border-gray-100">
                  <th className="text-right py-1 font-medium">חברה</th>
                  <th className="text-center py-1 font-medium">ריצות</th>
                  <th className="text-center py-1 font-medium">הושלמו</th>
                  <th className="text-center py-1 font-medium">ממתין לאישור</th>
                  <th className="text-center py-1 font-medium">ביטחון ממוצע</th>
                  <th className="text-center py-1 font-medium">שיעור עריכה</th>
                  <th className="text-center py-1 font-medium">דחייה%</th>
                </tr>
              </thead>
              <tbody>
                {shownCompanyStats.map((row) => {
                  const editPct = row.edit_rate != null ? Math.round(row.edit_rate * 100) : null;
                  const editCls = editPct == null ? "text-gray-300" : editPct <= 15 ? "text-green-600" : editPct <= 35 ? "text-amber-500" : "text-red-500";
                  const completedPct = row.total > 0 ? Math.round((row.completed / row.total) * 100) : null;
                  const rejectedPct = row.total > 0 ? Math.round((row.rejected / row.total) * 100) : null;
                  const rejectedCls = rejectedPct == null ? "text-gray-300" : rejectedPct <= 10 ? "text-green-600" : rejectedPct <= 25 ? "text-amber-500" : "text-red-500";
                  return (
                    <tr key={row.company_id ?? "none"} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-1.5 text-gray-700 max-w-[12rem] truncate">
                        {row.company_id ? (
                          <div className="flex items-center gap-1.5">
                            <Link
                              to={`/companies/${row.company_id}/profile`}
                              className="hover:underline text-brand-600"
                              title="פרופיל חברה"
                            >
                              {row.company_name}
                            </Link>
                            <Link
                              to={`/agent-runs?company_id=${row.company_id}`}
                              className="text-xs text-gray-400 hover:text-brand-600 shrink-0"
                              title="ריצות AI לחברה זו"
                            >
                              ↗
                            </Link>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic">{row.company_name}</span>
                        )}
                      </td>
                      <td className="py-1.5 text-center text-gray-600">{row.total}</td>
                      <td className="py-1.5 text-center">
                        <span className={completedPct == null ? "text-gray-300" : completedPct >= 70 ? "text-green-600" : completedPct >= 50 ? "text-amber-500" : "text-red-500"}>
                          {completedPct != null ? `${completedPct}%` : "—"}
                        </span>
                      </td>
                      <td className="py-1.5 text-center">
                        {row.pending_review > 0 ? (
                          <span className="bg-amber-100 text-amber-700 border border-amber-200 rounded-full px-1.5 py-0.5 font-medium">
                            {row.pending_review}
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className={`py-1.5 text-center font-medium ${
                        row.avg_confidence == null ? "text-gray-300"
                        : row.avg_confidence >= 70 ? "text-green-600"
                        : row.avg_confidence >= 50 ? "text-amber-500"
                        : "text-red-500"
                      }`}>
                        {row.avg_confidence != null ? `${row.avg_confidence}%` : "—"}
                      </td>
                      <td className={`py-1.5 text-center font-medium ${editCls}`}>
                        {editPct != null ? `${editPct}%` : "—"}
                      </td>
                      <td className={`py-1.5 text-center font-medium ${rejectedCls}`}>
                        {rejectedPct != null ? `${rejectedPct}%` : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Per-technician AI performance */}
      {technicianStatsFailed && <SectionLoadError text="לא ניתן לטעון את הנתונים לפי טכנאי" />}
      {technicianStats.length > 0 && (
        <div className="bg-white rounded-xl border shadow-sm p-5 space-y-3">
          <h2 className="font-semibold text-gray-700 text-sm">ביצועי AI לפי טכנאי</h2>
          <RowsProvenance
            prefix="הביטחון בטבלה זו"
            total={technicianAnalyses}
            degraded={technicianDegraded}
            testId="technician-provenance"
            covers="ביצועי AI לפי טכנאי"
          />
          <div className="overflow-x-auto">
            <table className="w-full text-xs" dir="rtl">
              <thead>
                <tr className="border-b text-gray-500 text-right">
                  <th className="pb-1.5 font-medium">טכנאי</th>
                  <th className="pb-1.5 font-medium text-center">סה״כ</th>
                  <th className="pb-1.5 font-medium text-center">הושלם%</th>
                  <th className="pb-1.5 font-medium text-center">ממתין</th>
                  <th className="pb-1.5 font-medium text-center">ביטחון</th>
                  <th className="pb-1.5 font-medium text-center">תיקון%</th>
                  <th className="pb-1.5 font-medium text-center">דחייה%</th>
                </tr>
              </thead>
              <tbody>
                {technicianStats.map((row) => {
                  const editPct = row.edit_rate != null ? Math.round(row.edit_rate * 100) : null;
                  const editCls = editPct == null ? "text-gray-300" : editPct <= 15 ? "text-green-600" : editPct <= 35 ? "text-amber-500" : "text-red-500";
                  const completedPct = row.total > 0 ? Math.round((row.completed / row.total) * 100) : null;
                  const rejectedPct = row.total > 0 ? Math.round((row.rejected / row.total) * 100) : null;
                  const rejectedCls = rejectedPct == null ? "text-gray-300" : rejectedPct <= 10 ? "text-green-600" : rejectedPct <= 25 ? "text-amber-500" : "text-red-500";
                  return (
                    <tr key={row.technician_id ?? "none"} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-1.5 text-gray-700 max-w-[12rem] truncate">
                        {row.technician_id ? (
                          <div className="flex items-center gap-1.5">
                            <span title={row.technician_name}>{row.technician_name}</span>
                            <Link
                              to={`/agent-runs?technician_id=${row.technician_id}`}
                              className="text-xs text-gray-400 hover:text-brand-600 shrink-0"
                              title="ריצות AI לטכנאי זה"
                            >
                              ↗
                            </Link>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic">{row.technician_name}</span>
                        )}
                      </td>
                      <td className="py-1.5 text-center text-gray-600">{row.total}</td>
                      <td className="py-1.5 text-center">
                        <span className={completedPct == null ? "text-gray-300" : completedPct >= 70 ? "text-green-600" : completedPct >= 50 ? "text-amber-500" : "text-red-500"}>
                          {completedPct != null ? `${completedPct}%` : "—"}
                        </span>
                      </td>
                      <td className="py-1.5 text-center">
                        {row.pending_review > 0 ? (
                          <span className="bg-amber-100 text-amber-700 border border-amber-200 rounded-full px-1.5 py-0.5 font-medium">
                            {row.pending_review}
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className={`py-1.5 text-center font-medium ${
                        row.avg_confidence == null ? "text-gray-300"
                        : row.avg_confidence >= 70 ? "text-green-600"
                        : row.avg_confidence >= 50 ? "text-amber-500"
                        : "text-red-500"
                      }`}>
                        {row.avg_confidence != null ? `${row.avg_confidence}%` : "—"}
                      </td>
                      <td className={`py-1.5 text-center font-medium ${editCls}`}>
                        {editPct != null ? `${editPct}%` : "—"}
                      </td>
                      <td className={`py-1.5 text-center font-medium ${rejectedCls}`}>
                        {rejectedPct != null ? `${rejectedPct}%` : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Recent corrections log ──────────────────────────────────────────── */}
      {recentCorrectionsFailed && <SectionLoadError text="לא ניתן לטעון את יומן התיקונים האחרונים" />}
      {recentCorrections.length > 0 && (
        <div className="bg-white rounded-xl border shadow-sm p-5 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="font-semibold text-gray-700 text-sm">תיקונים אחרונים</h2>
            <select
              value={correctionEditType}
              onChange={(e) => setCorrectionEditType(e.target.value)}
              className="text-xs border rounded px-2 py-1 bg-white text-gray-600"
            >
              <option value="">כל הסוגים</option>
              {Object.entries(EDIT_TYPE_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
          <div className="divide-y text-xs">
            {recentCorrections.filter(c => !correctionEditType || c.edit_type === correctionEditType).map((c) => (
              <div key={c.id} className="py-2 space-y-1">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="flex items-center gap-1.5">
                    <span className="bg-gray-100 text-gray-600 border rounded px-1 py-0.5">
                      {EDIT_TYPE_LABELS[c.edit_type] ?? c.edit_type}
                    </span>
                    <span className="text-gray-400">{c.technician_id?.split("@")[0]}</span>
                  </span>
                  <span className="flex items-center gap-2 text-gray-400">
                    <span>{c.created_at ? parseUtc(c.created_at).toLocaleDateString("he-IL") : ""}</span>
                    <Link to={`/tickets/${c.ticket_id}`} className="text-brand-600 hover:underline">קריאה ↗</Link>
                  </span>
                </div>
                {c.ai_summary && (
                  <p className="text-gray-500 line-clamp-2">
                    <span className="font-medium text-gray-400">AI: </span>{c.ai_summary}
                  </p>
                )}
                {c.tech_summary && c.tech_summary !== c.ai_summary && (
                  <p className="text-gray-700 line-clamp-2">
                    <span className="font-medium text-brand-600">תיקון: </span>{c.tech_summary}
                  </p>
                )}
                {c.reason && (
                  <p className="text-amber-700 italic">"{c.reason}"</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
